import React from "react";
import { formatNumber, formatDate, formatDuration } from "../youtube";
import { TYPE_META } from "../classifier";

const PLATFORMS = [
  { id:"shorts", label:"Shorts", color:"#ef4444" },
  { id:"tiktok", label:"TikTok", color:"#14b8a6" },
  { id:"instagram", label:"Instagram", color:"#a855f7" },
];

export default function Overview({ ch, setDetail, setTab, setFetchConfirm, isAdmin }) {
  const { channels, activeId, videos, tracking, clips, manualRefresh, loading } = ch;
  const active = channels.find(c => c.id === activeId);
  if (!active) return null;

  const allClips = Object.values(clips).flat();

  // Distribution stats — clips marked as posted per platform
  const postedByPlatform = {
    shorts:    Object.values(clips).flat().filter(c => c.posted?.shorts).length,
    tiktok:    Object.values(clips).flat().filter(c => c.posted?.tiktok).length,
    instagram: Object.values(clips).flat().filter(c => c.posted?.instagram).length,
  };
  const totalPosted = Object.values(postedByPlatform).reduce((a, b) => a + b, 0);

  const totalViews = videos.reduce((s,v) => s+(v.viewCount||0), 0);
  const totalLikes = videos.reduce((s,v) => s+(v.likeCount||0), 0);
  const totalComments = videos.reduce((s,v) => s+(v.commentCount||0), 0);
  const avgViews = videos.length ? Math.round(totalViews/videos.length) : 0;
  const avgDuration = videos.length ? Math.round(videos.reduce((s,v)=>s+(v.durationSeconds||0),0)/videos.length) : 0;
  const engRate = totalViews > 0 ? ((totalLikes/totalViews)*100).toFixed(2) : "0.00";
  const top5 = [...videos].sort((a,b)=>(b.viewCount||0)-(a.viewCount||0)).slice(0,5);
  const recent5 = videos.slice(0,5);


  // ── Channel Health Score ─────────────────────────────────────────────────
  const healthScore = (() => {
    if (videos.length < 5) return null;

    // 1. Consistency — uploads per month (last 90 days)
    const now = Date.now();
    const recent90 = videos.filter(v => now - new Date(v.publishedAt) < 90*24*3600*1000);
    const uploadsPerMonth = recent90.length / 3;
    const consistencyScore = Math.min(100, Math.round((uploadsPerMonth / 4) * 100)); // 4/mo = 100

    // 2. Engagement — like rate vs channel average
    const engRateNum = totalViews > 0 ? (totalLikes/totalViews)*100 : 0;
    const engScore = Math.min(100, Math.round((engRateNum / 5) * 100)); // 5% = 100

    // 3. Clip coverage — % of videos with at least 1 clip
    const withClips = videos.filter(v => (clips[v.id]||[]).length > 0).length;
    const clipScore = videos.length > 0 ? Math.round((withClips/videos.length)*100) : 0;

    // 4. Transcript coverage
    const withTx = Object.keys(ch.transcripts||{}).filter(id => ch.transcripts[id]).length;
    const txScore = videos.length > 0 ? Math.round((withTx/videos.length)*100) : 0;

    // 5. Growth momentum — last 10 vs previous 10 avg views
    const sorted = [...videos].sort((a,b) => new Date(b.publishedAt) - new Date(a.publishedAt));
    const last10avg = sorted.slice(0,10).reduce((s,v)=>s+(v.viewCount||0),0)/Math.max(1,Math.min(10,sorted.length));
    const prev10avg = sorted.slice(10,20).reduce((s,v)=>s+(v.viewCount||0),0)/Math.max(1,Math.min(10,sorted.slice(10,20).length));
    const momentumScore = prev10avg > 0
      ? Math.min(100, Math.max(0, Math.round(50 + ((last10avg-prev10avg)/prev10avg)*50)))
      : 50;

    const overall = Math.round(
      consistencyScore * 0.25 +
      engScore        * 0.25 +
      clipScore       * 0.20 +
      txScore         * 0.15 +
      momentumScore   * 0.15
    );

    return {
      overall,
      components: [
        { label:"Consistency",   score:consistencyScore, tip:`${uploadsPerMonth.toFixed(1)} uploads/mo`,     weight:"25%" },
        { label:"Engagement",    score:engScore,         tip:`${engRateNum.toFixed(2)}% like rate`,          weight:"25%" },
        { label:"Clip coverage", score:clipScore,        tip:`${withClips}/${videos.length} episodes`,       weight:"20%" },
        { label:"Transcripts",   score:txScore,          tip:`${withTx}/${videos.length} episodes`,          weight:"15%" },
        { label:"Momentum",      score:momentumScore,    tip:last10avg>prev10avg?"Recent videos outperforming":"Recent videos underperforming", weight:"15%" },
      ],
    };
  })();

  const Stat = ({label, value, color="#f1f5f9", sub=null}) => (
    <div className="cc-stat">
      <div className="cc-stat-label">{label}</div>
      <div className="cc-stat-value" style={{color, fontSize:22}}>{value}</div>
      {sub && <div style={{fontSize:11,color:"#475569",marginTop:4}}>{sub}</div>}
    </div>
  );

  const VideoRow = ({v, i, last, onClick}) => (
    <div onClick={onClick} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",borderBottom:!last?"1px solid #1e293b":"none",cursor:"pointer"}}>
      <img src={v.thumbnailDefault} alt="" style={{width:52,height:29,objectFit:"cover",borderRadius:4,flexShrink:0}}/>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:12,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:"#e2e8f0"}}>{v.title}</div>
        <div style={{fontSize:11,color:"#64748b",marginTop:2}}>{formatNumber(v.viewCount)} views · {formatDate(v.publishedAt)}</div>
      </div>
      <a href={`https://youtube.com/watch?v=${v.id}`} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{color:"#475569",fontSize:12,flexShrink:0}}>↗</a>
    </div>
  );

  return (
    <div>
      {/* Quick Actions */}
      {isAdmin && (
        <div className="cc-card" style={{marginBottom:20,padding:16}}>
          <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.14em",color:"#5A6A7E",marginBottom:12}}>
            Quick Actions
          </div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <button onClick={() => manualRefresh && manualRefresh()}
              disabled={loading}
              style={{padding:"9px 16px",background:"none",border:"1px solid #2B5EA7",borderRadius:4,color:"#2B5EA7",fontFamily:"inherit",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",cursor:"pointer",opacity:loading?0.5:1}}>
              ⟳ Refresh Videos
            </button>
            {setFetchConfirm && videos.length === 0 && (
              <button onClick={() => setFetchConfirm(true)}
                style={{padding:"9px 16px",background:"#2B5EA7",border:"1px solid #2B5EA7",borderRadius:4,color:"#E8EBF0",fontFamily:"inherit",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",cursor:"pointer"}}>
                ▶ Fetch Video Library
              </button>
            )}
            <button onClick={() => setTab("classify")}
              style={{padding:"9px 16px",background:"none",border:"1px solid #C9A227",borderRadius:4,color:"#C9A227",fontFamily:"inherit",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",cursor:"pointer"}}>
              ◐ Run AI Classify
            </button>
            <button onClick={() => setTab("analytics")}
              style={{padding:"9px 16px",background:"none",border:"1px solid #1A2940",borderRadius:4,color:"#5A6A7E",fontFamily:"inherit",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",cursor:"pointer"}}>
              ◉ View Analytics
            </button>
            <button onClick={() => setTab("calendar")}
              style={{padding:"9px 16px",background:"none",border:"1px solid #1A2940",borderRadius:4,color:"#5A6A7E",fontFamily:"inherit",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",cursor:"pointer"}}>
              ⊞ Content Calendar
            </button>
          </div>
        </div>
      )}

      {/* Channel Health Score */}
      {healthScore && (
        <div className="cc-card" style={{marginBottom:20,padding:20}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:12}}>
            <div>
              <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.14em",color:"#5A6A7E",marginBottom:4}}>
                Channel Health Score
              </div>
              <div style={{fontSize:11,color:"#3A4A5E"}}>Based on consistency, engagement, coverage & momentum</div>
            </div>
            <div style={{display:"flex",alignItems:"baseline",gap:6}}>
              <span style={{
                fontFamily:"Impact,'Arial Black',sans-serif",fontSize:48,lineHeight:1,
                color:healthScore.overall>=80?"#34d399":healthScore.overall>=60?"#C9A227":"#D93025"
              }}>{healthScore.overall}</span>
              <span style={{fontSize:16,color:"#5A6A7E"}}>/100</span>
            </div>
          </div>

          {/* Overall bar */}
          <div style={{height:8,background:"#080C12",borderRadius:4,overflow:"hidden",marginBottom:20}}>
            <div style={{
              height:"100%",borderRadius:4,transition:"width 0.5s",
              width:`${healthScore.overall}%`,
              background:healthScore.overall>=80?"#34d399":healthScore.overall>=60?"#C9A227":"#D93025"
            }}/>
          </div>

          {/* Component breakdown */}
          <div className="cc-health-grid" style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8}}>
            {healthScore.components.map(c => (
              <div key={c.label} style={{textAlign:"center"}}>
                <div style={{
                  fontFamily:"Impact,'Arial Black',sans-serif",fontSize:22,lineHeight:1,marginBottom:4,
                  color:c.score>=80?"#34d399":c.score>=60?"#C9A227":"#D93025"
                }}>{c.score}</div>
                <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:"#5A6A7E",marginBottom:3}}>{c.label}</div>
                <div style={{height:3,background:"#080C12",borderRadius:2,overflow:"hidden",marginBottom:4}}>
                  <div style={{height:"100%",width:`${c.score}%`,borderRadius:2,
                    background:c.score>=80?"#34d399":c.score>=60?"#C9A227":"#D93025"}}/>
                </div>
                <div style={{fontSize:10,color:"#3A4A5E",lineHeight:1.4}}>{c.tip}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:24}}>
        {active.thumbnail && <img src={active.thumbnail} alt="" style={{width:56,height:56,borderRadius:"50%",border:"2px solid #334155"}}/>}
        <div>
          <div style={{fontSize:22,fontWeight:700,color:"#f1f5f9"}}>{active.title||active.name}</div>
          <div style={{fontSize:13,color:"#64748b",marginTop:2}}>{formatNumber(active.subscriberCount)} subscribers · {formatNumber(active.videoCount)} videos</div>
        </div>
      </div>

      <div className="cc-grid-4" style={{marginBottom:12}}>
        <Stat label="Videos loaded" value={videos.length.toLocaleString()} color="#60a5fa"/>
        <Stat label="Total views" value={formatNumber(totalViews)} color="#34d399"/>
        <Stat label="Total likes" value={formatNumber(totalLikes)} color="#f472b6"/>
        <Stat label="Total comments" value={formatNumber(totalComments)} color="#fb923c"/>
      </div>
      <div className="cc-grid-4" style={{marginBottom:12}}>
        <Stat label="Avg views/video" value={formatNumber(avgViews)} color="#94a3b8"/>
        <Stat label="Avg duration" value={formatDuration(avgDuration)} color="#94a3b8"/>
        <Stat label="Engagement rate" value={engRate+"%"} color="#94a3b8" sub="likes ÷ views"/>
        <Stat label="Clips tracked" value={allClips.length}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:24}}>
        <Stat label="Reviewed" value={`${videos.filter(v=>tracking[v.id]?.reviewed).length} / ${videos.length}`} color="#fbbf24"/>
        <Stat label="With transcript" value={`${videos.filter(v=>tracking[v.id]?.transcript).length} / ${videos.length}`} color="#a78bfa"/>
        <Stat label="Shorts detected" value={videos.filter(v=>v.isShort).length} color="#ef4444"/>
        <Stat label="Clips done" value={allClips.filter(c=>c.status==="done").length} color="#34d399"/>
      </div>


      {/* Episode type breakdown */}
      {(() => {
        const schema = ch.classificationSchema || Object.keys(TYPE_META).map(id => ({...TYPE_META[id], id}));
        const counts = {};
        videos.forEach(v => {
          const vt = ch.getVideoType(v);
          counts[vt.type] = (counts[vt.type] || 0) + 1;
        });
        const total = videos.length || 1;
        const sorted = schema.filter(t => counts[t.id] > 0).sort((a,b) => (counts[b.id]||0) - (counts[a.id]||0));
        if (sorted.length === 0) return null;
        return (
          <div className="cc-card" style={{marginBottom:16,padding:16}}>
            <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.14em",color:"#5A6A7E",marginBottom:12}}>Episode Type Breakdown</div>
            {/* Bar chart */}
            <div style={{display:"flex",height:8,borderRadius:4,overflow:"hidden",marginBottom:16}}>
              {sorted.map(t => (
                <div key={t.id} style={{width:`${((counts[t.id]||0)/total*100).toFixed(1)}%`,background:t.color,transition:"width 0.3s"}} title={`${t.label}: ${counts[t.id]}`}/>
              ))}
            </div>
            {/* Legend */}
            <div style={{display:"flex",flexWrap:"wrap",gap:"8px 16px"}}>
              {sorted.map(t => (
                <div key={t.id} style={{display:"flex",alignItems:"center",gap:6}}>
                  <div style={{width:8,height:8,borderRadius:2,background:t.color,flexShrink:0}}/>
                  <span style={{fontSize:11,color:"#94a3b8"}}>{t.label}</span>
                  <span style={{fontSize:12,fontWeight:700,color:t.color}}>{counts[t.id]||0}</span>
                  <span style={{fontSize:10,color:"#475569"}}>({((counts[t.id]||0)/total*100).toFixed(0)}%)</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

            <div className="cc-grid-2" style={{marginBottom:16}}>

        {/* Distribution tracker summary */}
        {totalPosted > 0 && (
          <div className="cc-card" style={{marginBottom:16,padding:16}}>
            <div className="cc-card-header" style={{marginBottom:12}}>Distribution</div>
            <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
              {[
                {id:"shorts",   label:"Shorts",    color:"#ef4444"},
                {id:"tiktok",   label:"TikTok",    color:"#14b8a6"},
                {id:"instagram",label:"Instagram", color:"#a855f7"},
              ].map(p => (
                <div key={p.id} style={{display:"flex",alignItems:"center",gap:8,
                  padding:"8px 14px",background:p.color+"11",border:`1px solid ${p.color}44`,
                  borderRadius:8,flex:1,minWidth:100}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:p.color,flexShrink:0}}/>
                  <div>
                    <div style={{fontSize:18,fontWeight:700,color:p.color,lineHeight:1}}>
                      {postedByPlatform[p.id]}
                    </div>
                    <div style={{fontSize:10,color:"#5A6A7E",textTransform:"uppercase",
                      letterSpacing:"0.1em",fontWeight:700}}>{p.label}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{fontSize:11,color:"#5A6A7E",marginTop:10}}>
              {totalPosted} clip{totalPosted!==1?"s":""} posted across all platforms
            </div>
          </div>
        )}
        <div className="cc-card">
          <div className="cc-card-header">Top 5 by views</div>
          {top5.map((v,i)=><VideoRow key={v.id} v={v} i={i} last={i===top5.length-1} onClick={()=>{setDetail(v);setTab("episodes");}}/>)}
        </div>
        <div className="cc-card">
          <div className="cc-card-header">Recent uploads</div>
          {recent5.map((v,i)=><VideoRow key={v.id} v={v} i={i} last={i===recent5.length-1} onClick={()=>{setDetail(v);setTab("episodes");}}/>)}
        </div>
      </div>

      <div className="cc-grid-3">
        {PLATFORMS.map(p=>{
          const count=videos.filter(v=>(tracking[v.id]||{})[p.id]).length;
          return (
            <div key={p.id} className="cc-stat" style={{borderColor:p.color+"44"}}>
              <div className="cc-stat-label" style={{color:p.color}}>{p.label}</div>
              <div className="cc-stat-value" style={{color:p.color,fontSize:28}}>{count}</div>
              <div style={{fontSize:11,color:"#475569",marginTop:4}}>episodes tagged</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
